/**
 * A browser selection, as a block and a range of characters in it.
 *
 * The characters are counted in the text the SERVER matches against (the
 * block's inline text, `normalize.ts` `inlineText`), not in what the DOM
 * shows, because the two differ in exactly three places and the anchor has
 * to be in the server's terms:
 *
 *   * a chip displays one thing and matches as another (a mention with no
 *     label shows "@you" and matches "@person"), so it counts as its
 *     `data-len`, all or nothing -- a selection ending inside a chip takes
 *     the whole chip;
 *   * a highlight's pin button is not text at all, so it counts as nothing;
 *   * everything else is a text node whose data IS the model's text, because
 *     Marked.svelte renders `{text}` verbatim.
 *
 * MULTI-BLOCK SELECTIONS ARE CLIPPED TO THE FIRST BLOCK with any text in the
 * selection. The server anchors to one block (`resolveAnchor` matches within a
 * single block's text), so a range across two cannot be stored. Clipping rather
 * than refusing is deliberate: triple-clicking a paragraph -- the most natural
 * way to select one, and what the user's screenshot shows -- produces a range
 * that ends at offset 0 of the NEXT block, and a refusal there would make the
 * commonest gesture fail. `clipped` is true only when the part cut off had words
 * in it, so the composer can say so.
 *
 * A RENDERED DIAGRAM has no `data-text-block`: mermaid replaced its source
 * with an SVG. `diagramTarget` is the second try, for a selection lying
 * wholly inside one diagram (TASK-045); a selection that also takes in
 * ordinary text is `selectionTarget`'s, and the diagram part is ignored.
 */

import { LABEL_SELECTOR, labelDisplay, labelKey } from './diagram'

export interface SelectionTarget {
  blockId: string
  start: number
  end: number
  clipped: boolean
}

/** How many server-text characters a node contributes. */
function lengthOf(node: Node): number {
  if (node.nodeType === Node.TEXT_NODE) return (node as Text).data.length
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element
    if (el.hasAttribute('data-anchor-pin')) return 0
    const len = el.getAttribute('data-len')
    if (len !== null) return Number(len) || 0
  }
  let n = 0
  for (const child of Array.from(node.childNodes)) n += lengthOf(child)
  return n
}

/** The offset of a boundary point within a text block, in server characters. */
export function offsetIn(block: Element, container: Node, offset: number): number {
  const range = document.createRange()
  range.setStart(block, 0)
  range.setEnd(container, offset)
  return lengthOf(range.cloneContents())
}

/** Whether the part of `range` inside `el` has any non-whitespace text. */
function hasWords(el: Element, range: Range): boolean {
  const part = document.createRange()
  part.selectNodeContents(el)
  if (el.contains(range.startContainer)) part.setStart(range.startContainer, range.startOffset)
  if (el.contains(range.endContainer)) part.setEnd(range.endContainer, range.endOffset)
  return part.toString().trim().length > 0
}

/**
 * The first text block the selection has words in, and the selected characters
 * of it. Null when the selection is collapsed, outside `root`, or has no words
 * in any text block.
 */
export function selectionTarget(root: Element, range: Range): SelectionTarget | null {
  if (range.collapsed) return null
  if (!root.contains(range.commonAncestorContainer) && !range.commonAncestorContainer.contains(root)) return null
  const blocks = Array.from(root.querySelectorAll('[data-text-block]')).filter((el) => range.intersectsNode(el))

  let found: SelectionTarget | null = null
  for (const el of blocks) {
    const id = el.getAttribute('data-text-block')
    if (!id || !hasWords(el, range)) continue
    if (found) {
      found.clipped = true
      break
    }
    const start = el.contains(range.startContainer) ? offsetIn(el, range.startContainer, range.startOffset) : 0
    const end = el.contains(range.endContainer) ? offsetIn(el, range.endContainer, range.endOffset) : lengthOf(el)
    found = { blockId: id, start, end, clipped: false }
  }
  return found
}

/** The text of the part of `range` inside `el`, by boundary comparison. */
function partIn(el: Element, range: Range): string {
  const part = document.createRange()
  part.selectNodeContents(el)
  if (range.compareBoundaryPoints(Range.START_TO_START, part) > 0) part.setStart(range.startContainer, range.startOffset)
  // Setting an end before the start collapses the part: nothing of `el` is in it.
  if (range.compareBoundaryPoints(Range.END_TO_END, part) < 0) part.setEnd(range.endContainer, range.endOffset)
  return part.toString()
}

export interface DiagramTarget {
  /** The diagram's code block. */
  blockId: string
  /** The `textContent` of the one label selected in; null for none, or several. */
  label: string | null
  /** That label as a person reads it. */
  display: string | null
}

/**
 * A selection lying wholly inside one rendered diagram (its host carries
 * `data-diagram`), and the label it is in, if it is in exactly one: the only
 * label with words in the selection, holding all of the selection's words.
 * Null when the selection is collapsed, has no words, or is not inside a
 * diagram in `root`.
 */
export function diagramTarget(root: Element, range: Range): DiagramTarget | null {
  if (range.collapsed) return null
  const common = range.commonAncestorContainer
  const el = common.nodeType === Node.ELEMENT_NODE ? (common as Element) : common.parentElement
  const host = el?.closest('[data-diagram]')
  const blockId = host?.getAttribute('data-diagram')
  if (!host || !blockId || !root.contains(host)) return null
  const selected = labelKey(range.toString())
  if (!selected) return null

  const hit = Array.from(host.querySelectorAll<HTMLElement>(LABEL_SELECTOR)).filter(
    (label) => labelKey(label.textContent) && labelKey(partIn(label, range)),
  )
  const only = hit.length === 1 && labelKey(hit[0].textContent).includes(selected) ? hit[0] : null
  return only
    ? { blockId, label: only.textContent, display: labelDisplay(only.innerText || only.textContent) }
    : { blockId, label: null, display: null }
}
